import { defineStore } from 'pinia';
import { ref } from 'vue';
import { cloneDeep } from 'lodash';

import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { RecipeStateKey, TripStateKey } from '@apps/main/modules/state/state';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  IngredientId,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';

import { HotelSelectionEventType } from '../../../pages/hotelSelection/models';
import type { HotelSelectionEventTypes } from '../../../pages/hotelSelection/models';
import { HotelFilter } from '../../../modules/hotelFilters';
import type { HotelFilterUpdate } from '../../../modules/hotelFilters';
import type { RemoveHotelSelectionFiltersOrigin } from '../../models';

import { handleFilterUpdate } from './hotelFilters.logic';

export const useFiltersStore = defineStore('filtersStore', () => {
  const editingIngredientStore = useEditingIngredientStore();
  const statsigEventsStore = useStatsigEventsStore();
  const tripState = injectStrict(TripStateKey);
  const recipeState = injectStrict(RecipeStateKey);

  const editedHotelIngredient = ref<HotelIngredient | null>(null);

  function updateHotelFilter(
    filterUpdate: HotelFilterUpdate,
    origin: RemoveHotelSelectionFiltersOrigin,
    updateOriginalIngredient = true
  ): void {
    const hostname = window.location.origin;
    const pathname = window.location.pathname.split(hostname)[0] ?? '';

    if (editedHotelIngredient.value === null) {
      editedHotelIngredient.value = cloneDeep(
        editingIngredientStore.editingHotelIngredient
      );
    }

    // Polygon ID is needed for tracking, case here should not exists for filters, but as we have possible undefined in parrent types, default fallback is 0
    // If we detect in statsig that we have cases where polygonId is 0, we should add a check for that case
    const polygonId = editedHotelIngredient.value?.ingredientId
      ? tripState.getMainPolygonId(editedHotelIngredient.value?.ingredientId) ??
        0
      : 0;

    if (!editedHotelIngredient.value) {
      return;
    }

    const type = filterUpdate.type;
    const filterGroup = filterUpdate.type.split('.')[0] ?? '';
    const filterName = filterUpdate.type.split('.')[1] ?? '';
    const filterDetail =
      typeof filterUpdate.value !== 'boolean'
        ? filterUpdate.value.toString()
        : '';
    filterUpdate.value
      ? handleEvents({
          type: HotelSelectionEventType.ApplyFilter,
          data: {
            filterName: filterName ?? '',
            filterGroup: filterGroup ?? '',
            filterDetail: filterDetail,
            polygonId,
            pathname,
          },
        })
      : handleEvents({
          type: HotelSelectionEventType.RemoveFilter,
          data: {
            filterName: filterName ?? '',
            filterGroup: filterGroup ?? '',
            filterDetail: filterDetail,
            polygonId,
            pathname,
            eventPlacement: origin,
          },
        });

    handleFilterUpdate(type, editedHotelIngredient.value, filterUpdate.value);

    if (
      updateOriginalIngredient &&
      editingIngredientStore.editingHotelIngredient
    ) {
      editingIngredientStore.editingHotelIngredient.filters =
        editedHotelIngredient.value?.filters;
    }
  }

  function getChangedHotelIngredient(): HotelIngredient | null {
    return editedHotelIngredient.value;
  }

  function clearEditedHotelIngredient(): void {
    editedHotelIngredient.value = null;
  }

  function setHotelIngredientForFiltering(): void {
    editedHotelIngredient.value = cloneDeep(
      editingIngredientStore.editingHotelIngredient
    );
  }

  function hasActiveHotelFilters(): boolean {
    return editedHotelIngredient.value?.hasActiveFilters() ?? false;
  }

  function activeHotelFiltersLength(): number {
    return editedHotelIngredient.value?.getActiveFiltersCount() ?? 0;
  }

  function handleEvents(event: HotelSelectionEventTypes): void {
    switch (event.type) {
      case HotelSelectionEventType.Initialized:
        statsigEventsStore.logHotelSelectionInitiated(event.data);
        break;

      case HotelSelectionEventType.OpenFilters:
        statsigEventsStore.logHotelSelectionFilterOpened(event.data);
        break;

      case HotelSelectionEventType.CloseFilters:
        statsigEventsStore.logHotelSelectionFilterClosed(event.data);
        break;

      case HotelSelectionEventType.ApplyFilter:
        statsigEventsStore.logHotelSelectionFilterApplied(event.data);
        break;

      case HotelSelectionEventType.RemoveFilter:
        statsigEventsStore.logHotelSelectionFilterRemoved(event.data);
        break;

      case HotelSelectionEventType.ResetFilter:
        statsigEventsStore.logHotelSelectionFilterReset(event.data);
        break;

      default:
        break;
    }
  }

  function removeFilterFromTripOverview(
    hotelFilter: HotelFilter,
    ingredientId: IngredientId
  ): void {
    const hotelIngredient = recipeState.getHotelIngredient(ingredientId);
    editingIngredientStore.setHotelEditingIngredient(hotelIngredient);

    let filterUpdate: string | StarsRatingFilter | boolean;
    switch (hotelFilter) {
      case HotelFilter.RatingStars:
        filterUpdate = 0;
        break;
      case HotelFilter.HotelName:
        filterUpdate = '';
        break;
      default:
        filterUpdate = false;
        break;
    }
    handleFilterUpdate(hotelFilter, hotelIngredient, filterUpdate);
    editedHotelIngredient.value = null;
  }

  function resetAccommodationFilters(): void {
    editingIngredientStore.editingHotelIngredient?.resetAllFilters();
    editedHotelIngredient.value = cloneDeep(
      editingIngredientStore.editingHotelIngredient
    );
  }

  return {
    updateHotelFilter,
    getChangedHotelIngredient,
    clearEditedHotelIngredient,
    removeFilterFromTripOverview,
    resetAccommodationFilters,
    hasActiveHotelFilters,
    activeHotelFiltersLength,
    setHotelIngredientForFiltering,
    editedHotelIngredient,
  };
});
